import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Form, Field, FormElement } from "@progress/kendo-react-form";

import {
    FormAutoComplete,
} from "../../forms/form-components";

export const AdministratorForceRunTicketingWebsiteAlerts = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const [ticketingWebsiteAlertForcedRuns, setTicketingWebsiteAlertForcedRuns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [ticketingWebsiteAlerts, setTicketingWebsiteAlerts] = useState([]);
    const [ticketingWebsiteAlertName, setTicketingWebsiteAlertName] = useState(undefined);
    const [areTicketingWebsiteAlertsLoading, setAreTicketingWebsiteAlertsLoading] = useState(false);

    const autocompleteTimeout = useRef();
    const timeoutDelay = 500; //Milliseconds

    const DeleteCell = (props) => {
        const ticketingWebsiteAlertForcedRunKey = props.dataItem.ticketingWebsiteAlertForcedRunKey;

        return (
            <td className="text-nowrap">
                <Button type={"button"} icon="trash" title="Delete" onClick={() => deleteTWAForceRun(ticketingWebsiteAlertForcedRunKey)}>
                    Delete
                </Button>
            </td>
        );
    };
    
    const getTicketingWebsiteAlertForcedRuns = () => {
        setIsLoading(true);

        fetch(`/api/administrator/ticketingwebsitealertforcedruns`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setTicketingWebsiteAlertForcedRuns(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const deleteTWAForceRun = (ticketingWebsiteAlertForcedRunKey) => {
        if (window.confirm("Are you sure you want to delete this TWA forced run?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            ticketingWebsiteAlertForcedRunKey: ticketingWebsiteAlertForcedRunKey,
        };

        fetch(`/api/administrator/ticketingwebsitealertforcedrun`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "TWA force run deleted successfully");

                getTicketingWebsiteAlertForcedRuns();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const createTWAForceRun = (dataItem) => {
        if (!ticketingWebsiteAlertName) {
            addNotificationWithTimeout(dispatch, "error", "This is not a valid alert.");
            return;
        }

        dataItem.ticketingWebsiteAlertKey = undefined;
        if (ticketingWebsiteAlerts.length > 0) {
            const ticketingWebsiteAlert = ticketingWebsiteAlerts.find(element => element.label === ticketingWebsiteAlertName);

            if (ticketingWebsiteAlert) {
                dataItem.ticketingWebsiteAlertKey = ticketingWebsiteAlert.ticketingWebsiteAlertKey;
            }
        }

        if (!dataItem.ticketingWebsiteAlertKey) {
            addNotificationWithTimeout(dispatch, "error", "This is not a valid alert.");
            return;
        }

        setIsLoading(true);

        fetch(`/api/administrator/ticketingwebsitealertforcedrun`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                getTicketingWebsiteAlertForcedRuns();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const ticketingWebsiteAlertChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setTicketingWebsiteAlerts([]);
            setAreTicketingWebsiteAlertsLoading(false);
            setTicketingWebsiteAlertName(null);
            return;
        }

        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                isActive: true,
            };

            fetch(`/api/administrator/ticketingwebsitealerts`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setTicketingWebsiteAlerts(result.ticketingWebsiteAlerts);
                    setTicketingWebsiteAlertName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreTicketingWebsiteAlertsLoading(false);
                });
        }, timeoutDelay);

        setAreTicketingWebsiteAlertsLoading(true);
    };
    
    useEffect(() => {
        document.title = "Force Run TWAs";

        getTicketingWebsiteAlertForcedRuns();
    }, []);

    return (
        <div className="administratorReport">
            {isLoading ? <LoadingPanel /> : <></>}

            <Form
                onSubmit={createTWAForceRun}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">
                                <div className="col-md-10">
                                    <Field
                                        id={"ticketingWebsiteAlert"}
                                        name={"ticketingWebsiteAlert"}
                                        label={"Alert"}
                                        component={FormAutoComplete}
                                        data={ticketingWebsiteAlerts}
                                        placeholder="e.g. Roseland"
                                        textField="label"
                                        onChange={ticketingWebsiteAlertChanged}
                                        loading={areTicketingWebsiteAlertsLoading}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="play"
                                        >
                                            Run Alert
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </FormElement>
                )}
            />

            <Grid
                data={ticketingWebsiteAlertForcedRuns}                
                total={ticketingWebsiteAlertForcedRuns.length}
                resizable={true}
                pageable={false}
                scrollable="none"
            >
                <Column cell={DeleteCell} />
                <Column field="ticketingWebsiteAlertLabel" title={"Alert"} />
                <Column field="dateCreated" title={"Force run date"} />
            </Grid>
        </div>
    );
}